import type { ContentNode, ContentField } from "@/types/template";

export interface TemplateMeta {
  name: string;
  id?: string;
  category?: string;
  variables?: string[];
  isSubTemplate?: boolean;
}

function serializeTextField(field: Extract<ContentField, { type: "text" }>): string {
  const params: string[] = ["text" + (field.optional ? "?" : "")];
  if (field.multiline) {
    params.push("multiline");
  }
  if (field.defaultValue) {
    const escaped = field.defaultValue.replaceAll("\n", String.raw`\n`);
    params.push(`default=${escaped}`);
  }
  return [`@field(${params.join(", ")})`, field.label].join("\n");
}

function serializeSelectField(field: Extract<ContentField, { type: "select" }>): string {
  const lines = ["@field(select)", field.label];
  for (const option of field.options) {
    lines.push(`  ${option}`);
  }
  return lines.join("\n");
}

function serializeCheckboxListField(
  field: Extract<ContentField, { type: "checkbox-list" }>,
): string {
  const params: string[] = ["checklist"];
  if (field.defaultAll) {
    params.push("defaultAll");
  }
  const lines = [`@field(${params.join(", ")})`, field.label];
  for (const item of field.items) {
    lines.push(`  ${item}`);
  }
  return lines.join("\n");
}

function serializeStatementListField(
  field: Extract<ContentField, { type: "statement-list" }>,
): string {
  const lines = ["@field(statement-list)", field.label];
  for (const item of field.items) {
    lines.push(`  ${item}`);
  }
  return lines.join("\n");
}

function serializeScoreField(field: Extract<ContentField, { type: "score" }>): string {
  const min = field.min ?? 0;
  const max = field.max;
  const params: string[] = ["score", `${min}-${max}`];
  if (field.pairId) {
    params.push(`group=${field.pairId}`);
  }
  if (field.pairAgg) {
    params.push(`agg=${field.pairAgg}`);
  }
  const lines = [`@field(${params.join(", ")})`, field.label];
  if (field.scoreOptions) {
    for (const opt of field.scoreOptions) {
      lines.push(`  [${opt.value}] ${opt.label}`);
    }
  }
  return lines.join("\n");
}

function serializeGridField(field: Extract<ContentField, { type: "grid" }>): string {
  const lines = ["@field(grid)", field.label, `  | | ${field.columns.join(" | ")} |`];
  for (const row of field.rows) {
    const cells = field.columns.map((c) => row.defaults?.[c] ?? "");
    lines.push(`  | ${row.label} | ${cells.join(" | ")} |`);
  }
  return lines.join("\n");
}

function serializeDateField(field: Extract<ContentField, { type: "date" }>): string {
  const params: string[] = ["date" + (field.optional ? "?" : "")];
  if (field.defaultValue) {
    params.push(`default=${field.defaultValue}`);
  }
  return [`@field(${params.join(", ")})`, field.label].join("\n");
}

function serializeField(field: ContentField): string {
  switch (field.type) {
    case "text": {
      return serializeTextField(field);
    }
    case "select": {
      return serializeSelectField(field);
    }
    case "checkbox-list": {
      return serializeCheckboxListField(field);
    }
    case "statement-list": {
      return serializeStatementListField(field);
    }
    case "score": {
      return serializeScoreField(field);
    }
    case "grid": {
      return serializeGridField(field);
    }
    case "sub-template": {
      return [`@field(sub-template, ${field.templateId})`, field.label].join("\n");
    }
    case "date": {
      return serializeDateField(field);
    }
  }
}

function serializeFrontmatter(meta: TemplateMeta): string {
  const fm: string[] = ["---", `name: ${meta.name}`];
  if (meta.id) {
    fm.push(`id: ${meta.id}`);
  }
  if (meta.category) {
    fm.push(`category: ${meta.category}`);
  }
  if (meta.variables && meta.variables.length > 0) {
    fm.push(`variables: ${meta.variables.join(", ")}`);
  }
  if (meta.isSubTemplate) {
    fm.push("sub-template: true");
  }
  fm.push("---");
  return fm.join("\n");
}

export function serializeTemplate(nodes: ContentNode[], meta: TemplateMeta): string {
  const parts: string[] = [serializeFrontmatter(meta)];

  // Content nodes
  for (const node of nodes) {
    switch (node.type) {
      case "section": {
        const req = node.required ? ", required" : "";
        parts.push(`@section(title=${node.title}${req})`);
        break;
      }
      case "heading": {
        const prefix = "#".repeat(node.level);
        parts.push(`${prefix} ${node.text}`);
        break;
      }
      case "text": {
        parts.push(node.markdown);
        break;
      }
      case "scoregroup": {
        parts.push(`@scoregroup(${node.id})`);
        break;
      }
      case "field": {
        parts.push(serializeField(node.field));
        break;
      }
    }
  }

  return parts.join("\n") + "\n";
}
